import React, { PureComponent } from 'react'
import PropTypes from 'prop-types'
import toArray from 'lodash-es/toArray'
import pick from 'lodash-es/pick'
import isEmpty from 'lodash-es/isEmpty'
import size from 'lodash-es/size'
import reduce from 'lodash-es/reduce'
import keys from 'lodash-es/keys'
import PictureFill from 'picturefill' // picture element polyfill for older browser support
import { MEDIA_SIZES as sizes } from 'aria-viewer/tour/constants/index'
import PhotoPrompt from './components/photoPrompt'
import { WithForwardRef, BackgroundImageOnLoad } from 'aria-viewer/common'
import compose from 'recompose/compose'

export const PHOTOPLAYER = 'photoplayer'
export const PHOTOPLAY_BLURIMAGE = 'photoplayer_blurimage'

class PhotoPlayer extends PureComponent {
    static propTypes = {
        options: PropTypes.object.isRequired,
        alt: PropTypes.string,
        style: PropTypes.object,
        onLoad: PropTypes.func,
        onError: PropTypes.func,
    }

    state = {
        down: false,
    }

    onMouseDown = () => {
        this.setState({ down: true })
    }

    onMouseUp = () => {
        this.setState({ down: false })
    }

    onMouseMove = e => {
        if (this.state.down) {
            this.setState({
                down: false,
                position: {
                    left: e.pageX,
                    top: e.pageY,
                },
            })
            setTimeout(() => {
                this.setState({
                    position: null,
                })
            }, 5000)
        }
    }

    onLoad = e => {
        if (this.props.onLoad) {
            this.props.onLoad(e, this.props.forwardRef.current)
        }
    }

    render() {
        const blur = this.blurredImage()
        const img = this.getImg()

        return (
            <div className={this.props.className}>
                {blur}
                <div
                    style={{
                        width: '100%',
                        height: '100%',
                        left: '50%',
                        top: '50%',
                        transform: 'translate(-50%, -50%)',
                        WebkitBackfaceVisibility: 'hidden',
                        position: 'absolute',
                    }}
                >
                    {img}
                </div>
                <PhotoPrompt
                    sizes={this.props.sizes}
                    position={this.state.position}
                    onMouseMove={this.onMouseMove}
                    onMouseDown={this.onMouseDown}
                    onMouseUp={this.onMouseUp}
                />
            </div>
        )
    }

    getImg() {
        const { options } = this.props
        const { urls, size } = options

        const src = this.getSrc(urls, size)
        const sizesDesired = toArray(sizes).reverse()
        const desiredImages = pick(urls, sizesDesired)

        // If image src is specified then render it.
        if (!isEmpty(src)) {
            return this.renderImage(src)
        }

        // Else if only a single image available then render that.
        else if (size(desiredImages) == 1) {
            return this.renderImage(reduce(desiredImages, (result, value, key) => value))
        }

        // Otherwise handle images responsively.
        else {
            return this.renderResponsiveImages(desiredImages)
        }
    }

    /**
     * Returns an image source if matching size in urls object is present.
     *
     * @param  object urls 		An object with different sizes of image urls based on sizes.
     * @param  string size 		Size of image desired.
     * @return string | null    Url desired or null if size doesn't exists.
     */
    getSrc(urls, size) {
        /*******
		TODO: When we standardize how we reference media sizes
		ex: medium vs meduimurl
		we need to also standardize what props get passed
		********/
        if (size in sizes) {
            return urls[size] || urls[`${size}url`]
        }

        return null
    }

    /**
     * Renders an fill image blurred in backfround
     *
     * @param  string src 	 The url of the image to load.
     * @return dom node    	 Image dom element.
     */
    blurredImage() {
        const { options, forwardRef } = this.props
        if (this.props.options.disableBlur) {
            return null
        }

        const { urls, size } = options
        const src = this.getSrc(urls, size)
        const blurNum = 18
        return (
            <img
                id={PHOTOPLAY_BLURIMAGE}
                ref={forwardRef}
                aria-hidden="true"
                src={src}
                alt="main media blurred for appearance"
                style={{
                    objectFit: 'cover',
                    width: '100%',
                    height: '100%',
                    filter: `blur(${blurNum}px)`,
                    WebkitFilter: `blur(${blurNum}px)`,
                }}
            />
        )
    }

    /**
     * Renders an image tag with the specified src.
     *
     * @param  string src 	 The url of the image to load.
     * @return dom node    	 Image dom element.
     */
    renderImage(src) {
        const { style, imgClassName, alt, children, onError } = this.props

        return (
            <BackgroundImageOnLoad src={src} onLoad={this.onLoad} onError={onError}>
                {({ imageLoaded }) => {
                    return imageLoaded ? (
                        <div
                            id={PHOTOPLAYER}
                            className={imgClassName}
                            alt={alt}
                            style={{
                                backgroundImage: `url(${src})`,
                                backgroundRepeat: 'no-repeat',
                                backgroundPosition: '50% 50%',
                                ...style,
                            }}
                        >
                            {children}
                        </div>
                    ) : null
                }}
            </BackgroundImageOnLoad>
        )
    }

    /**
     * Renders a picture element with all the image sources to be responsively displayed.
     *
     * @param  string urls   An object with images to load.
     * @return dom node      Picture dom element.
     */
    renderResponsiveImages(urls) {
        const { style, alt, children, options } = this.props

        const defaultMinWidth = {
            thumb: '200px',
            tiny: '400px',
            small: '800px',
            medium: '1000px',
            large: '1200px',
        }

        const urlKeys = keys(urls)
        const images = toArray(urls)
        const minWidth = Object.assign({}, defaultMinWidth, options.minWidth)

        return (
            <picture>
                {images.slice(0, size(images) - 1).map((image, index) => {
                    return (
                        <source
                            key={`image-${index}`}
                            srcSet={image}
                            media={`(min-width: ${minWidth[urlKeys[index]]})`}
                        />
                    )
                })}

                <img
                    id={PHOTOPLAYER}
                    srcSet={images.slice(-1)[0]}
                    alt={alt}
                    style={style}
                    onLoad={this.onLoad}
                    onError={this.props.onError}
                >
                    {children}
                </img>
            </picture>
        )
    }
}

export default compose(
    WithForwardRef,
    Component => ({ forwardRef, ...props }) => (
        <Component forwardRef={forwardRef ? forwardRef : React.createRef()} {...props} />
    )
)(PhotoPlayer)
